import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Timer, Heart, Flame, Path, Mountains, Gauge, TrendUp, TrendDown, CheckCircle, Info, Lightning } from "@phosphor-icons/react";

const zoneColors = ["hsl(220 10% 35%)", "#4dabf5", "#00e676", "#ffea00", "#ff9100", "#ff3b30"];
const zoneDefs = [
  ["Зона 0", "< 50% макс. пульса · отдых"],
  ["Зона 1", "50–60% · разминка, восстановление"],
  ["Зона 2", "60–70% · жиросжигание, база"],
  ["Зона 3", "70–80% · аэробная выносливость"],
  ["Зона 4", "80–90% · темп, анаэробный порог"],
  ["Зона 5", "90–100% · максимум, VO₂max"],
];

function workoutVerdict(w) {
  const total = w.zones.reduce((a, b) => a + b, 0);
  if (!total) return { title: "Нет данных по пульсовым зонам", text: "Whoop не записал зоны для этой тренировки.", level: "info" };
  const z45 = (w.zones[4] + w.zones[5]) / total;
  const z3 = w.zones[3] / total;
  const z12 = (w.zones[1] + w.zones[2]) / total;
  if (z45 >= 0.2) return { title: "Высокоинтенсивная тренировка", text: `${Math.round(z45 * 100)}% времени в зонах 4–5 — развитие VO₂max и анаэробного порога. После такой сессии дайте организму 1–2 дня на восстановление.`, level: "alert" };
  if (z3 >= 0.3) return { title: "Темповая аэробная тренировка", text: `${Math.round(z3 * 100)}% времени в зоне 3 — развитие выносливости. Хорошая нагрузка для роста аэробной базы.`, level: "success" };
  if (z12 >= 0.5) return { title: "Базовая / восстановительная", text: `${Math.round(z12 * 100)}% времени в зонах 1–2 — жиросжигание и активное восстановление без стресса для организма.`, level: "success" };
  return { title: "Лёгкая активность", text: "Большая часть времени — в низких зонах. Отличный вариант для дня отдыха.", level: "info" };
}

const levelIcon = {
  success: <CheckCircle size={20} weight="fill" className="text-[#00e676] shrink-0" />,
  alert: <Lightning size={20} weight="fill" className="text-[#ff9100] shrink-0" />,
  info: <Info size={20} weight="fill" className="text-primary shrink-0" />,
};

function Cmp({ label, value, avg, unit, invert }) {
  if (value == null) return null;
  const pct = avg ? ((value - avg) / Math.abs(avg)) * 100 : null;
  return (
    <div className="flex items-center justify-between text-xs py-1.5 border-b border-border/40 last:border-b-0">
      <span className="text-muted-foreground">{label}</span>
      <span className="flex items-center gap-2 num">
        {pct != null && Math.abs(pct) >= 5 && (
          <span className={`flex items-center gap-0.5 ${pct > 0 !== !!invert ? "text-[#00e676]" : "text-[#ffea00]"}`}>
            {pct > 0 ? <TrendUp size={11} /> : <TrendDown size={11} />}{Math.abs(pct).toFixed(0)}%
          </span>
        )}
        <span className="font-semibold">{value}{unit ? ` ${unit}` : ""}</span>
        {avg != null && <span className="text-muted-foreground">ср. {Math.round(avg * 10) / 10}</span>}
      </span>
    </div>
  );
}

export default function WorkoutDetailSheet({ workout, workouts, maxHr, onClose }) {
  const w = workout;
  const avgOf = (key) => {
    const vals = (workouts || []).map((x) => x[key]).filter((v) => v != null);
    return vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null;
  };
  const verdict = w ? workoutVerdict(w) : null;
  const totalZ = w ? w.zones.reduce((a, b) => a + b, 0) : 0;

  return (
    <Sheet open={!!w} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="glass-modal w-full sm:max-w-xl overflow-y-auto" data-testid="workout-detail-sheet">
        {w && (
          <>
            <SheetHeader>
              <SheetTitle className="font-heading text-2xl">{w.sport}</SheetTitle>
              <SheetDescription className="text-muted-foreground num">
                {w.date.slice(8, 10)}.{w.date.slice(5, 7)}.{w.date.slice(0, 4)} · {w.time} · {w.duration_min} мин
                {w.percent_recorded != null && w.percent_recorded < 100 && ` · записано ${w.percent_recorded}%`}
              </SheetDescription>
            </SheetHeader>

            <div className="mt-4 grid grid-cols-3 gap-2">
              {[
                [<Gauge size={15} className="text-primary" />, "Нагрузка", w.strain, ""],
                [<Flame size={15} className="text-[#ffea00]" />, "Калории", w.kcal, "ккал"],
                [<Heart size={15} className="text-[#ff3b30]" />, "Средний пульс", w.avg_hr, "уд/мин"],
                [<Heart size={15} weight="fill" className="text-[#ff3b30]" />, "Макс. пульс", w.max_hr, maxHr && w.max_hr ? `(${Math.round((w.max_hr / maxHr) * 100)}% от макс.)` : ""],
                [<Path size={15} className="text-[#4dabf5]" />, "Дистанция", w.distance_km, "км"],
                [<Mountains size={15} className="text-[#00e676]" />, "Набор высоты", w.altitude_m, "м"],
              ].map(([icon, label, v, unit], i) => (
                <div key={i} className="rounded-md bg-secondary/60 p-2.5">
                  <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-muted-foreground mb-1">{icon}{label}</div>
                  <div className="num text-sm font-semibold">{v != null ? `${v} ${unit}` : "—"}</div>
                </div>
              ))}
            </div>

            {verdict && (
              <div className="mt-4 rounded-lg border border-border/60 p-4 flex gap-3" data-testid="workout-verdict">
                {levelIcon[verdict.level]}
                <div>
                  <div className="text-sm font-semibold mb-1">{verdict.title}</div>
                  <p className="text-xs text-muted-foreground leading-relaxed">{verdict.text}</p>
                </div>
              </div>
            )}

            <div className="mt-4">
              <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Пульсовые зоны</div>
              {totalZ === 0 ? (
                <p className="text-xs text-muted-foreground">Нет данных по зонам</p>
              ) : (
                <div className="space-y-2" data-testid="workout-zones">
                  {w.zones.map((z, i) => (
                    <div key={i}>
                      <div className="flex justify-between text-[11px] mb-0.5">
                        <span><span className="font-semibold">{zoneDefs[i][0]}</span> <span className="text-muted-foreground">· {zoneDefs[i][1]}</span></span>
                        <span className="num">{z} мин · {Math.round((z / totalZ) * 100)}%</span>
                      </div>
                      <div className="h-2 bg-secondary rounded-full overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${(z / totalZ) * 100}%`, background: zoneColors[i] }} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="mt-4">
              <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Сравнение с вашей средней тренировкой</div>
              <Cmp label="Нагрузка" value={w.strain} avg={avgOf("strain")} unit="" />
              <Cmp label="Длительность" value={w.duration_min} avg={avgOf("duration_min")} unit="мин" />
              <Cmp label="Калории" value={w.kcal} avg={avgOf("kcal")} unit="ккал" />
              <Cmp label="Средний пульс" value={w.avg_hr} avg={avgOf("avg_hr")} unit="уд/мин" />
              {w.kcal != null && w.duration_min > 0 && (
                <Cmp label="Интенсивность (ккал/мин)" value={Math.round((w.kcal / w.duration_min) * 10) / 10}
                  avg={avgOf("kcal") && avgOf("duration_min") ? avgOf("kcal") / avgOf("duration_min") : null} unit="" />
              )}
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
